import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider.js';
import { useCollectionPickerOptions } from '../data/queries.js';
import { getEffectiveOcrConfig } from '../import/api.js';
import { CollectionPicker } from '../import/CollectionPicker.js';
import { OcrSetupGuide } from '../import/OcrSetupGuide.js';
import type { ScannedPage } from '../import/pageMarker.js';
import { scanPages } from '../import/scanPages.js';
import { uploadBatch, type UploadProgress } from '../import/uploadBatch.js';
import { useSync } from '../local/SyncProvider.js';
import { reportError } from '../sentry.js';
import { DEFAULT_MODEL_BY_PROVIDER } from '../settings/ocrSettings.js';

type Phase = 'config' | 'scanning' | 'uploading';

/**
 * Mobile-first "Scan pages" entry: pick an optional target cookbook, then
 * rapid-capture pages with the live viewfinder ({@link scanPages}) and feed
 * them straight into the standard OCR pipeline ({@link uploadBatch}). The
 * OCR provider/model are resolved silently so there's no config form on the
 * phone.
 */
export function ScanPagesPage() {
  const { user } = useAuth();
  const { syncNow } = useSync();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: pickerOptions = [], isLoading: pickerLoading } = useCollectionPickerOptions();
  // `?collection=` pre-scopes the scan to the cookbook the user came from
  // (the "Scan pages" button on a collection page), so they don't re-pick it.
  const [targetCollectionId, setTargetCollectionId] = useState(
    () => searchParams.get('collection') ?? '',
  );
  const [needsSetup, setNeedsSetup] = useState(false);
  const [phase, setPhase] = useState<Phase>('config');
  const [progress, setProgress] = useState<UploadProgress | undefined>();
  const [error, setError] = useState<string | undefined>();

  // Resolve OCR config silently — the user configures nothing here. A null
  // result means no own key and no household share, so surface the guide.
  useEffect(() => {
    let cancelled = false;
    void getEffectiveOcrConfig()
      .then((cfg) => {
        if (!cancelled) setNeedsSetup(cfg === null);
      })
      .catch(() => {
        if (!cancelled) setNeedsSetup(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onScan() {
    if (!user) return;
    setError(undefined);
    setPhase('scanning');
    let pages: ScannedPage[];
    try {
      pages = await scanPages({ maxShots: 200 });
    } catch (e) {
      setError((e as Error).message);
      setPhase('config');
      return;
    }
    if (pages.length === 0) {
      setPhase('config');
      return;
    }
    await upload(pages);
  }

  async function upload(pages: ScannedPage[]) {
    if (!user) return;
    setPhase('uploading');
    setError(undefined);
    try {
      // own prefs+key, else the household's shared config, else gemini
      // defaults. getEffectiveOcrConfig resolves the effective fallback
      // (own → localStorage "Fallback model"; household → its own).
      const cfg = await getEffectiveOcrConfig().catch(() => null);
      const fallbackProvider = cfg?.fallbackProvider ?? null;
      const fallbackModel = cfg?.fallbackModel ?? null;
      const defaultProvider = cfg?.provider ?? 'gemini';
      // Capture-time chaining (the camera's ⛓ toggle) is carried to the
      // organizer as an initial merge state — NOT folded at upload — so every
      // page uploads as its own AWAITING_GROUPING item the user can still
      // un-merge. `initialMerges` is the list of split indices to remove (a
      // page that joins its predecessor removes the split before it).
      const initialMerges = pages.flatMap((p, i) =>
        i > 0 && p.marker.joinsPrevious ? [i - 1] : [],
      );
      // A single page has nothing to group — send it straight to OCR and drop
      // the reviewer on the item, which is what the old collection-page
      // "Take photo" button did. Its captured kind still rides along below.
      const singlePage = pages.length === 1;
      const result = await uploadBatch(
        {
          ownerId: user.id,
          name: `Scan ${new Date().toLocaleString()}`,
          targetCollectionId: targetCollectionId || null,
          defaultProvider,
          defaultModel: cfg?.model || DEFAULT_MODEL_BY_PROVIDER[defaultProvider],
          defaultEndpoint: cfg?.endpoint ?? null,
          defaultPrompt: cfg?.prompt ?? null,
          fallbackProvider,
          fallbackModel,
          fallbackEndpoint: cfg?.fallbackEndpoint ?? null,
          keyOwnerId: cfg?.source === 'household' ? cfg.keyOwnerId : null,
          sourceKind: 'IMAGES',
          files: pages.map((p) => p.file),
          // Carry the camera's ▤ Contents / Notes choice onto the item rows so
          // the worker uses the right prompt and the organizer shows it
          // pre-selected. `joinsPrevious` is deliberately NOT folded here —
          // chaining stays undoable via `initialMerges` on the next screen.
          markers: pages.map((p) => ({ kind: p.marker.kind, joinsPrevious: false })),
          awaitGrouping: !singlePage,
        },
        setProgress,
      );
      const { batchId, itemIds } = result;
      await syncNow();
      if (singlePage && itemIds[0]) {
        navigate(`/import/${batchId}/items/${itemIds[0]}`);
      } else {
        navigate(`/import/${batchId}/group`, { state: { initialMerges } });
      }
    } catch (e) {
      reportError(e, { operation: 'batch_upload', tags: { source: 'scan' } });
      setError((e as Error).message);
      setPhase('config');
    }
  }

  if (phase === 'uploading') {
    const phaseLabel =
      progress?.phase === 'preparing'
        ? 'Preparing pages'
        : progress?.phase === 'uploading'
          ? 'Uploading'
          : progress?.phase === 'finalizing'
            ? 'Finalizing'
            : 'Done';
    const pct =
      progress && progress.total > 0
        ? Math.min(100, Math.round((progress.done / progress.total) * 100))
        : 0;
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Uploading…</h1>
        <p className="text-sm text-stone-600 dark:text-stone-400">
          Don't close this tab — we're uploading your scanned pages.
        </p>
        <div>
          <div className="flex justify-between text-sm text-stone-700 dark:text-stone-300">
            <span>{phaseLabel}</span>
            <span>
              {progress?.done ?? 0} / {progress?.total ?? 0}
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-700">
            <div className="h-full bg-stone-900 dark:bg-stone-100" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {error && <p className="text-sm text-red-700 dark:text-red-300">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/import"
        className="inline-block text-sm text-stone-500 underline-offset-2 hover:underline dark:text-stone-400"
      >
        ← Add recipes
      </Link>
      <h1 className="text-2xl font-semibold">Scan pages</h1>
      <p className="text-sm text-stone-600 dark:text-stone-400">
        Point your camera at a cookbook and tap the shutter for each page — no need to stop between
        shots. Use the <span className="font-medium">⛓ Join</span> shutter to continue a multi-page
        recipe and the <span className="font-medium">▤ Contents</span> shutter for a
        table-of-contents page. When you're done, you'll group the pages into recipes on the next
        screen.
      </p>

      {needsSetup && <OcrSetupGuide />}

      <div className="max-w-md space-y-1">
        <span className="block text-sm font-medium text-stone-700 dark:text-stone-300">
          Target cookbook (optional)
        </span>
        <CollectionPicker
          options={pickerOptions}
          value={targetCollectionId}
          onChange={setTargetCollectionId}
          loading={pickerLoading}
        />
        <p className="text-xs text-stone-500 dark:text-stone-400">
          Leave unassigned to sort pages into cookbooks later.
        </p>
      </div>

      <button
        type="button"
        onClick={onScan}
        disabled={phase === 'scanning'}
        className="inline-flex items-center gap-2 rounded-md bg-stone-900 dark:bg-stone-100 px-4 py-2.5 text-sm font-medium text-white dark:text-stone-900 hover:bg-stone-800 dark:hover:bg-stone-200 disabled:opacity-60"
      >
        <span aria-hidden>📷</span>
        {phase === 'scanning' ? 'Opening camera…' : 'Scan pages'}
      </button>

      {error && <p className="text-sm text-red-700 dark:text-red-300">{error}</p>}
    </div>
  );
}
